import React, { useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import ActionToggle from 'live/components/ui/action-toggle'
import { FontStyle } from './FontStyle'
import { InputType } from './InputType'
import { ValueOptionsInterface, TypeVariableOptions } from '../types'
import { LABEL_POSITION_OPTIONS } from '../constants'

type valueOptionsProps = {
    valueOptions: ValueOptionsInterface
    index?: number
    changeValueOptions: (
        valueOptions: ValueOptionsInterface,
        configItemType: string,
        value: any,
        itemKey: string,
        indexSecondaryValue?: number
    ) => void
    variableOptions: TypeVariableOptions
    setIsIconChoserOpen: (value: boolean) => void
    setSaveIconAux: (index: number) => void
}

export const ValueOptions = ({
    valueOptions,
    changeValueOptions,
    index,
    variableOptions,
    setIsIconChoserOpen,
    setSaveIconAux
}: valueOptionsProps): JSX.Element => {
    const { variable, prefix, sufix, trend, label } = valueOptions

    const [prefixIsEnabled, setPrefixIsEnabled] = useState(prefix.isEnabled)
    const [sufixIsEnabled, setSufixIsEnabled] = useState(sufix.isEnabled)
    const [trendIsEnabled, setTrendIsEnabled] = useState(trend.isEnabled)
    const [labelIsEnabled, setLabelIsEnabled] = useState(label.isEnabled)
    const [labelIconIsEnabled, setLabelIconIsEnabled] = useState(label.isIconEnabled)

    const changeValueConfig = (
        value: any,
        configItemType: string,
        configItemKey: string,
        setValueState?: (value) => void
    ) => {
        setValueState && setValueState(value)
        changeValueOptions(valueOptions, configItemType, value, configItemKey, index)
    }

    return (
        <Container>
            <TitleDiv>{i18n('Value options').toUpperCase()}</TitleDiv>

            <SubContainer id="variable">
                <RowContainer>
                    <InputType
                        inputType="selector"
                        textLabel="Variable"
                        placeholder="Select the variable"
                        value={variable.value}
                        options={variableOptions}
                        onChange={value => changeValueConfig(value, 'variable', 'value')}
                    />
                </RowContainer>

                <VerticalDivLine isLineTransparent={true} />

                <RowContainer>
                    <InputType
                        inputType="color-picker"
                        textLabel="Font color"
                        value={variable.fontColor}
                        onChange={color => changeValueConfig(color, 'variable', 'fontColor')}
                    />
                    <FontStyle
                        isEnabled={true}
                        isBold={variable.isBold}
                        isItalic={variable.isItalic}
                        changeValueConfig={changeValueConfig}
                        configItemType={'variable'}
                    />
                </RowContainer>
            </SubContainer>

            <HorizontalDivLine isLineTransparent={true} />

            <SubContainer id="prefix-sufix">
                <RowContainer id="prefix">
                    <ColumnContainer>
                        <ToggleDiv>
                            <ActionToggle
                                active={prefixIsEnabled}
                                onChange={() => {
                                    changeValueConfig(!prefixIsEnabled, 'prefix', 'isEnabled', setPrefixIsEnabled)
                                }}
                            />
                            <ToggleLabel>{i18n('Enable Prefix')}</ToggleLabel>
                        </ToggleDiv>

                        <RowDiv>
                            <InputType
                                inputType="text"
                                textLabel="Prefix Name"
                                value={prefix.value}
                                placeholder={'Insert prefix'}
                                onChange={event => {
                                    const value = event.target.value
                                    changeValueConfig(value, 'prefix', 'value')
                                }}
                            />
                        </RowDiv>

                        <RowDiv>
                            <FontConfig>
                                <InputType
                                    inputType="color-picker"
                                    textLabel="Font color"
                                    value={prefix.fontColor}
                                    onChange={color => changeValueConfig(color, 'prefix', 'fontColor')}
                                />
                                <FontStyle
                                    isEnabled={prefixIsEnabled}
                                    isBold={prefix.isBold}
                                    isItalic={prefix.isItalic}
                                    changeValueConfig={changeValueConfig}
                                    configItemType={'prefix'}
                                />
                            </FontConfig>
                        </RowDiv>
                    </ColumnContainer>
                </RowContainer>

                <VerticalDivLine />

                <RowContainer id="sufix">
                    <ColumnContainer>
                        <ToggleDiv>
                            <ActionToggle
                                active={sufixIsEnabled}
                                onChange={() => {
                                    changeValueConfig(!sufixIsEnabled, 'sufix', 'isEnabled', setSufixIsEnabled)
                                }}
                            />
                            <ToggleLabel>{i18n('Enable Sufix')}</ToggleLabel>
                        </ToggleDiv>
                        <RowDiv>
                            <InputType
                                inputType="text"
                                textLabel="Sufix Name"
                                value={sufix.value}
                                placeholder={'Insert sufix'}
                                onChange={event => {
                                    const value = event.target.value
                                    changeValueConfig(value, 'sufix', 'value')
                                }}
                            />
                        </RowDiv>
                        <RowDiv>
                            <FontConfig>
                                <InputType
                                    inputType="color-picker"
                                    textLabel="Font color"
                                    value={sufix.fontColor}
                                    onChange={color => changeValueConfig(color, 'sufix', 'fontColor')}
                                />
                                <FontStyle
                                    isEnabled={sufixIsEnabled}
                                    isBold={sufix.isBold}
                                    isItalic={sufix.isItalic}
                                    changeValueConfig={changeValueConfig}
                                    configItemType={'sufix'}
                                />
                            </FontConfig>
                        </RowDiv>
                    </ColumnContainer>
                </RowContainer>
            </SubContainer>

            <HorizontalDivLine />

            <SubContainer id="trend">
                <RowContainer>
                    <ColumnContainer>
                        <ToggleDiv>
                            <ActionToggle
                                active={trendIsEnabled}
                                onChange={() => {
                                    changeValueConfig(!trendIsEnabled, 'trend', 'isEnabled', setTrendIsEnabled)
                                }}
                            />
                            <ToggleLabel>{i18n('Enable Trend')}</ToggleLabel>
                        </ToggleDiv>
                        <ToggleDetails>
                            {i18n(
                                'This trend can be customized through query pipes. You have to select these variables: “a”, “b".'
                            )}
                        </ToggleDetails>
                    </ColumnContainer>
                </RowContainer>

                <VerticalDivLine isLineTransparent={true} />

                <RowContainer id="trend">
                    <InputType
                        inputType="text"
                        textLabel="Tooltip Message"
                        placeholder="Tooltip trend message"
                        value={trend.tooltipMessage}
                        onChange={event => {
                            const value = event.target.value
                            changeValueConfig(value, 'trend', 'tooltipMessage')
                        }}
                    />
                </RowContainer>
            </SubContainer>

            <HorizontalDivLine />

            <SubContainer id="label">
                <RowContainer>
                    <ToggleDiv>
                        <ActionToggle
                            active={labelIsEnabled}
                            onChange={() => {
                                changeValueConfig(!labelIsEnabled, 'label', 'isEnabled', setLabelIsEnabled)
                            }}
                        />
                        <ToggleLabel>{i18n('Use Label')}</ToggleLabel>
                    </ToggleDiv>
                </RowContainer>
            </SubContainer>

            <RowContainer id="label">
                <RowContainer>
                    <ColumnContainer>
                        <RowDiv>
                            <LabelConfig>
                                <InputType
                                    inputType="text"
                                    textLabel="Label Name"
                                    placeholder="Text label name"
                                    value={label.value}
                                    onChange={event => {
                                        const value = event.target.value
                                        changeValueConfig(value, 'label', 'value')
                                    }}
                                />

                                <VerticalDivLine isLineTransparent={true} />

                                <InputType
                                    inputType="selector"
                                    textLabel="Label Position"
                                    placeholder="Label Position"
                                    value={label.position}
                                    options={LABEL_POSITION_OPTIONS}
                                    onChange={value => changeValueConfig(value, 'label', 'position')}
                                />
                            </LabelConfig>
                        </RowDiv>

                        <RowDiv>
                            <FontConfig>
                                <InputType
                                    inputType="color-picker"
                                    textLabel="Font color"
                                    value={label.fontColor}
                                    onChange={color => changeValueConfig(color, 'label', 'fontColor')}
                                />
                                <FontStyle
                                    isEnabled={labelIsEnabled}
                                    isBold={label.isBold}
                                    isItalic={label.isItalic}
                                    changeValueConfig={changeValueConfig}
                                    configItemType={'label'}
                                />
                            </FontConfig>
                        </RowDiv>
                    </ColumnContainer>
                </RowContainer>

                <VerticalDivLine isLineTransparent={true} />

                <RowContainer>
                    <ColumnContainer className="label-config">
                        <ToggleDiv>
                            <ActionToggle
                                active={labelIconIsEnabled}
                                onChange={() => {
                                    changeValueConfig(
                                        !labelIconIsEnabled,
                                        'label',
                                        'isIconEnabled',
                                        setLabelIconIsEnabled
                                    )
                                }}
                            />
                            <ToggleLabel>{i18n('Use Label Icon')}</ToggleLabel>
                        </ToggleDiv>

                        <LabelConfig>
                            <InputType
                                inputType="color-picker"
                                textLabel="Font color"
                                value={label.iconColor}
                                onChange={color => changeValueConfig(color, 'label', 'iconColor')}
                            />

                            <VerticalDivLine isLineTransparent={true} />

                            <IconButton
                                isIconEnabled={label.isIconEnabled}
                                disabled={!label.isIconEnabled}
                                onClick={() => {
                                    setIsIconChoserOpen(true)
                                    setSaveIconAux(index)
                                }}
                            >
                                {i18n('Choose Icon')}
                            </IconButton>
                        </LabelConfig>
                    </ColumnContainer>
                </RowContainer>
            </RowContainer>
        </Container>
    )
}

export const Container = styled.div`
    width: 100%;
    width: 572px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme?.['gray-100'] || '#F5F5F5'};
`

export const SubContainer = styled.div`
    padding: 8px;
    width: 100%;
    margin: 0px !important;
    display: flex;
    justify-content: space-between;
`

export const RowContainer = styled.div`
    padding: 0px 8px;
    width: 100%;
    margin: 0px !important;
    display: flex;
    justify-content: space-between;

    .label-config {
        justify-content: flex-start !important;
    }
`

export const ColumnContainer = styled(SubContainer)`
    padding: 0px;
    flex-direction: column;
    justify-content: space-between;
`

export const TitleDiv = styled.div`
    padding: 8px 16px;
    font-weight: 700;
    color: ${({ theme }) => theme?.['gray-700'] || '#606A80'};
    margin: 0px !important;
`

export const RowDiv = styled.div`
    padding-bottom: 8px;
    width: 100%;
`

export const ToggleDiv = styled.div`
    padding-bottom: 8px;
    width: 100%;
`

export const FontConfig = styled.div`
    width: 100%;
    margin: 0px !important;
    display: flex;
    justify-content: space-between;
`

export const PrefixConfig = styled.div`
    width: 100%;
    margin: 0px !important;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

export const SufixConfig = styled.div`
    width: 100%;
    margin: 0px !important;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

export const ToggleLabel = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme?.['gray-800'] || '#364159'};
    padding-left: 3px;
`

export const ToggleDetails = styled.span`
    font-size: 12px;
    font-weight: 400;
    font-style: italic;
    color: ${({ theme }) => theme?.['gray-700'] || '#606A80'};
`

export const TrendDiv = styled.div`
    width: 100%;
    margin: 0px !important;
    display: flex;
    justify-content: space-between;
`

export const LabelConfig = styled.div`
    width: 100%;
    margin: 0px !important;
    display: flex;
    justify-content: space-between;
`

export const LabelNameConfig = styled.div`
    width: 100%;
    margin: 0px !important;
    display: flex;
    justify-content: space-between;
`

export const HorizontalDivLine = styled.div<{ isLineTransparent?: boolean }>`
    position: static;
    width: auto;
    height: 1px;
    background-color: ${({ isLineTransparent, theme }) => (isLineTransparent ? '' : theme?.['gray-400'] || '#C8CCD4')};
    margin: 8px 0px;
`

export const VerticalDivLine = styled.div<{ isLineTransparent?: boolean }>`
    position: static;
    width: 1px;
    height: auto;
    background-color: ${({ isLineTransparent, theme }) => (isLineTransparent ? '' : theme?.['gray-400'] || '#C8CCD4')};
    margin: 0px 8px;
`

export const IconButton = styled.button<{ isIconEnabled?: boolean }>`
    width: 100%;
    height: 32px;
    align-self: flex-end;
    background-color: ${({ theme }) => theme?.['gray-100'] || '#F5F5F5'};
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme?.['gray-400'] || '#C8CCD4'};
    color: ${({ isIconEnabled, theme }) =>
        isIconEnabled ? theme?.['gray-800'] || '#364159' : theme?.['gray-500'] || '#BAC0CB'};
    font-weight: 600;
`
